import { useState, useEffect } from 'react';
import { X, Loader2, Target, Plus, Trash2, Search, CheckCircle, AlertCircle, BarChart3, TrendingUp, Eye } from 'lucide-react';
import { labApi } from '@/lib/api';
import type { LabCompetitor } from './types';
import { RADAR_AXES } from './constants';

interface CompetitorModalProps {
  productId: string;
  competitor?: LabCompetitor | null;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

interface AnalysisResult {
  pageName: string;
  totalAds: number;
  activeAds: number;
  radarScores: Record<string, number>;
  topAds: Array<{ title: string; activeDays: number; url?: string; body?: string }>;
  ads: Array<{
    id: string;
    body?: string;
    link?: string;
    platforms?: string[];
    startDate?: string;
    pageName?: string;
    impressionLowerBound?: number;
    impressionUpperBound?: number;
    spendLowerBound?: number;
    spendUpperBound?: number;
    isActive?: boolean;
  }>;
}

export function CompetitorModal({ productId, competitor, onSave, onClose }: CompetitorModalProps) {
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [adLibraryUrl, setAdLibraryUrl] = useState(competitor?.linkAdLibrary || '');
  const [form, setForm] = useState({
    name: competitor?.name || '',
    linkAdLibrary: competitor?.linkAdLibrary || '',
    linkLanding: competitor?.linkLanding || '',
    linkAdvertorial: competitor?.linkAdvertorial || '',
    linkQuiz: competitor?.linkQuiz || '',
    linkListicale: competitor?.linkListicale || '',
    otherLinks: (competitor?.otherLinks || []) as Array<{ label: string; url: string }>,
    radarScores: {
      activeAds: competitor?.radarScores?.activeAds ?? 0,
      adDuration: competitor?.radarScores?.adDuration ?? 0,
      formatVariety: competitor?.radarScores?.formatVariety ?? 0,
      newCreatives: competitor?.radarScores?.newCreatives ?? 0,
      platformReach: competitor?.radarScores?.platformReach ?? 0,
      engagement: competitor?.radarScores?.engagement ?? 0,
      budget: competitor?.radarScores?.budget ?? 0,
    },
    topAds: (competitor?.topAds || []) as Array<{ title: string; activeDays: number; url?: string; body?: string }>,
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleAnalyze = async () => {
    if (!adLibraryUrl.trim()) return;
    setAnalyzing(true);
    setAnalysisError(null);
    setAnalysisResult(null);

    try {
      const res = await labApi.analyzeCompetitor(productId, adLibraryUrl.trim());
      const analysis = res.data.data as AnalysisResult;
      setAnalysisResult(analysis);

      // Auto-fill form with scraped data
      setForm(p => ({
        ...p,
        name: p.name || analysis.pageName,
        linkAdLibrary: adLibraryUrl.trim(),
        radarScores: {
          activeAds: analysis.radarScores.activeAds ?? 0,
          adDuration: analysis.radarScores.adDuration ?? 0,
          formatVariety: analysis.radarScores.formatVariety ?? 0,
          newCreatives: analysis.radarScores.newCreatives ?? 0,
          platformReach: analysis.radarScores.platformReach ?? 0,
          engagement: analysis.radarScores.engagement ?? 0,
          budget: analysis.radarScores.budget ?? 0,
        },
        topAds: analysis.topAds.map(a => ({
          title: a.title,
          activeDays: a.activeDays,
          url: a.url || '',
          body: a.body || '',
        })),
      }));
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string }; status?: number }; message?: string };
      const serverMsg = axiosErr.response?.data?.error;
      const fallback = axiosErr.message || 'Nieznany błąd';
      setAnalysisError(serverMsg || fallback);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        linkAdLibrary: form.linkAdLibrary.trim() || null,
        linkLanding: form.linkLanding.trim() || null,
        linkAdvertorial: form.linkAdvertorial.trim() || null,
        linkQuiz: form.linkQuiz.trim() || null,
        linkListicale: form.linkListicale.trim() || null,
        otherLinks: form.otherLinks.filter(l => l.label.trim() && l.url.trim()),
        radarScores: form.radarScores,
        topAds: form.topAds.filter(a => a.title.trim()),
      });
    } finally {
      setSaving(false);
    }
  };

  const updateRadar = (key: string, value: number) => {
    setForm(p => ({
      ...p,
      radarScores: { ...p.radarScores, [key]: Math.max(0, Math.min(100, value)) },
    }));
  };

  const addTopAd = () => {
    if (form.topAds.length >= 5) return;
    setForm(p => ({ ...p, topAds: [...p.topAds, { title: '', activeDays: 0, url: '' }] }));
  };

  const updateTopAd = (idx: number, field: string, value: string | number) => {
    setForm(p => ({
      ...p,
      topAds: p.topAds.map((a, i) => i === idx ? { ...a, [field]: value } : a),
    }));
  };

  const removeTopAd = (idx: number) => {
    setForm(p => ({ ...p, topAds: p.topAds.filter((_, i) => i !== idx) }));
  };

  const addOtherLink = () => {
    setForm(p => ({ ...p, otherLinks: [...p.otherLinks, { label: '', url: '' }] }));
  };

  const updateOtherLink = (idx: number, field: string, value: string) => {
    setForm(p => ({
      ...p,
      otherLinks: p.otherLinks.map((l, i) => i === idx ? { ...l, [field]: value } : l),
    }));
  };

  const removeOtherLink = (idx: number) => {
    setForm(p => ({ ...p, otherLinks: p.otherLinks.filter((_, i) => i !== idx) }));
  };

  // Stats from analysis
  const totalSpend = analysisResult?.ads
    ? analysisResult.ads.reduce((s, a) => s + ((a.spendLowerBound || 0) + (a.spendUpperBound || 0)) / 2, 0)
    : 0;
  const totalImpressions = analysisResult?.ads
    ? analysisResult.ads.reduce((s, a) => s + ((a.impressionLowerBound || 0) + (a.impressionUpperBound || 0)) / 2, 0)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-8">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-3xl p-6 shadow-2xl my-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <Target className="w-4 h-4 text-accent-primary" />
            {competitor ? 'Edytuj konkurenta' : 'Dodaj konkurenta'}
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1 mt-1">
          {/* FB Ad Library Analysis — prominent section */}
          <div className="bg-gradient-to-r from-accent-primary/5 to-accent-secondary/5 border border-accent-primary/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-accent-primary" />
              <span className="text-sm font-medium text-text-primary">Analiza z Biblioteki Reklam Facebook</span>
            </div>
            <p className="text-xs text-text-secondary mb-3">
              Wklej link do Biblioteki Reklam lub ID strony — dane radaru, top reklamy i statystyki zostaną pobrane automatycznie.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={adLibraryUrl}
                onChange={e => setAdLibraryUrl(e.target.value)}
                placeholder="https://www.facebook.com/ads/library/?view_all_page_id=... lub ID strony"
                className="flex-1 px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
              />
              <button
                onClick={handleAnalyze}
                disabled={analyzing || !adLibraryUrl.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-accent-primary/20 text-accent-primary rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-accent-primary/30 transition-colors shrink-0"
              >
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                Analizuj
              </button>
            </div>

            {analysisError && (
              <div className="flex items-center gap-2 mt-3 text-xs text-danger bg-danger/10 rounded-lg px-3 py-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {analysisError}
              </div>
            )}

            {analysisResult && (
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2 text-xs text-success">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Dane pobrane dla: <span className="font-medium text-text-primary">{analysisResult.pageName}</span>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'Reklamy', value: analysisResult.totalAds, icon: BarChart3 },
                    { label: 'Aktywne', value: analysisResult.activeAds, icon: TrendingUp },
                    { label: 'Wyświetlenia', value: totalImpressions > 0 ? `~${Math.round(totalImpressions / 1000)}k` : '—', icon: Eye },
                    { label: 'Wydatki', value: totalSpend > 0 ? `~${Math.round(totalSpend)} PLN` : '—', icon: Target },
                  ].map(stat => (
                    <div key={stat.label} className="bg-surface-hover/50 rounded-lg p-2.5 text-center">
                      <stat.icon className="w-3.5 h-3.5 text-text-muted mx-auto mb-1" />
                      <div className="text-sm font-semibold text-text-primary">{stat.value}</div>
                      <div className="text-[10px] text-text-muted">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="text-xs text-text-muted mb-1.5 block">Nazwa konkurenta *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Nazwa konkurenta"
              className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
              autoFocus={!competitor}
            />
          </div>

          {/* Links */}
          <div>
            <label className="text-xs text-text-muted mb-2 block">Linki</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'linkAdLibrary', label: 'Biblioteka reklam' },
                { key: 'linkLanding', label: 'Landing page' },
                { key: 'linkAdvertorial', label: 'Advertorial' },
                { key: 'linkQuiz', label: 'Quiz' },
                { key: 'linkListicale', label: 'Listicale' },
              ].map(({ key, label }) => (
                <input
                  key={key}
                  type="text"
                  value={(form as Record<string, unknown>)[key] as string}
                  onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={label}
                  className="px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
                />
              ))}
            </div>
          </div>

          {/* Other links */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-text-muted">Inne linki</label>
              <button onClick={addOtherLink} className="text-xs text-accent-primary hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" /> Dodaj
              </button>
            </div>
            {form.otherLinks.map((link, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={link.label}
                  onChange={e => updateOtherLink(idx, 'label', e.target.value)}
                  placeholder="Etykieta"
                  className="flex-1 px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
                />
                <input
                  type="text"
                  value={link.url}
                  onChange={e => updateOtherLink(idx, 'url', e.target.value)}
                  placeholder="URL"
                  className="flex-1 px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
                />
                <button onClick={() => removeOtherLink(idx)} className="p-2 text-text-muted hover:text-danger">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          {/* Radar scores */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-text-muted">Radar konkurencji (0-100)</label>
              {analysisResult && (
                <span className="text-[10px] text-success bg-success/10 px-2 py-0.5 rounded">auto-wypełnione z FB</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {RADAR_AXES.map(axis => (
                <div key={axis.key} className="flex items-center gap-3">
                  <span className="text-xs text-text-secondary w-32 shrink-0">{axis.label}</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={form.radarScores[axis.key as keyof typeof form.radarScores]}
                    onChange={e => updateRadar(axis.key, parseInt(e.target.value))}
                    className="flex-1 accent-accent-primary h-1"
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.radarScores[axis.key as keyof typeof form.radarScores]}
                    onChange={e => updateRadar(axis.key, parseInt(e.target.value) || 0)}
                    className="w-14 px-2 py-1 bg-surface-hover border border-border rounded text-text-primary text-xs text-center focus:outline-none focus:border-accent-primary/50"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Top Ads */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-text-muted">Top reklamy (max 5)</label>
              <button
                onClick={addTopAd}
                disabled={form.topAds.length >= 5}
                className="text-xs text-accent-primary hover:underline flex items-center gap-1 disabled:opacity-50"
              >
                <Plus className="w-3 h-3" /> Dodaj
              </button>
            </div>
            {form.topAds.map((ad, idx) => (
              <div key={idx} className="mb-2 bg-surface-hover/30 rounded-lg p-2.5">
                <div className="flex gap-2 mb-1.5">
                  <input
                    type="text"
                    value={ad.title}
                    onChange={e => updateTopAd(idx, 'title', e.target.value)}
                    placeholder="Tytuł / tekst reklamy"
                    className="flex-1 px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
                  />
                  <input
                    type="number"
                    min={0}
                    value={ad.activeDays}
                    onChange={e => updateTopAd(idx, 'activeDays', parseInt(e.target.value) || 0)}
                    placeholder="Dni"
                    className="w-20 px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
                  />
                  <button onClick={() => removeTopAd(idx)} className="p-1.5 text-text-muted hover:text-danger">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {ad.url && (
                  <a
                    href={ad.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-accent-primary hover:underline truncate block"
                  >
                    {ad.url}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3 mt-5 pt-4 border-t border-border-subtle">
          <button
            onClick={handleSubmit}
            disabled={!form.name.trim() || saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-accent-primary/20 text-accent-primary rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-accent-primary/30 transition-colors"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Target className="w-3.5 h-3.5" />}
            {competitor ? 'Zapisz zmiany' : 'Dodaj konkurenta'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-text-secondary text-sm hover:text-text-primary transition-colors"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
